import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useImportCampaignSchedules } from '~/hooks/apis/offers/useImportCampaignSchedules';

import { formatDateISO } from '~/services/TimeService';

import CampaignScheduleValuePillCell from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/CampaignScheduleValuePillCell';
import { groupCandidatesByRegion } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleCandidateGroups';
import { getScheduleTypeLabel } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleHistoryDisplay';
import type { CampaignScheduleImportCandidatesResponse } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

type Props = {
  error?: unknown;
  importCandidates?: CampaignScheduleImportCandidatesResponse;
  isError: boolean;
  isLoading: boolean;
  offerId?: string;
  onClose: () => void;
  open: boolean;
};

export default function SalesforceCampaignSchedulesModal({
  error,
  importCandidates,
  isError,
  isLoading,
  offerId,
  onClose,
  open,
}: Props) {
  const candidates = importCandidates?.candidates ?? [];
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const importMutation = useImportCampaignSchedules(offerId);
  const wasOpen = useRef(open);

  useEffect(() => {
    // Only reset on the closed -> open transition — `candidates` also changes after every import (cache
    // invalidation), which would otherwise silently re-select candidates the admin had deselected.
    if (open && !wasOpen.current) {
      // Conflicting candidates are selectable but never auto-selected — overwriting an existing schedule's
      // dates is a deliberate action the admin should opt into, not something imported by default.
      setSelectedKeys(
        new Set(
          candidates
            .filter((candidate) => !candidate.isMatched && !candidate.dependencyError && !candidate.conflictingSchedule)
            .map((candidate) => candidate.key),
        ),
      );
      importMutation.reset();
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates]);

  const toggleKeys = (keys: Array<string>, select: boolean) => {
    setSelectedKeys((current) => {
      const next = new Set(current);
      keys.forEach((key) => (select ? next.add(key) : next.delete(key)));
      return next;
    });
  };

  const selectableCandidates = useMemo(
    () => candidates.filter((candidate) => !candidate.isMatched && !candidate.dependencyError),
    [candidates],
  );
  const allSelected =
    selectableCandidates.length > 0 && selectableCandidates.every((candidate) => selectedKeys.has(candidate.key));
  const someSelected = !allSelected && selectableCandidates.some((candidate) => selectedKeys.has(candidate.key));

  const toggleSelectAll = () => {
    setSelectedKeys(allSelected ? new Set() : new Set(selectableCandidates.map((candidate) => candidate.key)));
  };

  const handleImport = () => {
    const selectedCandidates = candidates.filter((candidate) => selectedKeys.has(candidate.key));
    importMutation.mutate(selectedCandidates);
  };

  const resultByKey = useMemo(
    () => new Map((importMutation.data?.results ?? []).map((result) => [result.candidate.key, result])),
    [importMutation.data],
  );
  const candidateGroups = useMemo(() => groupCandidatesByRegion(candidates, resultByKey), [candidates, resultByKey]);
  const allWarnings = importCandidates?.warnings ?? [];
  const skippedKeys = importMutation.data?.skippedKeys ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Schedules from Salesforce</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : 'Failed to load Salesforce campaign schedules'}
          </Alert>
        )}

        {!isLoading && !isError && importCandidates && (
          <Stack spacing={3}>
            {skippedKeys.length > 0 && (
              <Alert severity="warning">
                {skippedKeys.length} selected schedule{skippedKeys.length === 1 ? '' : 's'} could no longer be found
                (Salesforce or this offer's schedules changed) and {skippedKeys.length === 1 ? 'was' : 'were'} skipped.
              </Alert>
            )}

            {allWarnings.length > 0 && (
              <Stack spacing={1}>
                {allWarnings.map((warning) => (
                  <Alert key={warning} severity="warning">
                    {warning}
                  </Alert>
                ))}
              </Stack>
            )}

            <Box>
              <Typography variant="h6" gutterBottom>
                Schedules to import ({candidates.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        disabled={selectableCandidates.length === 0 || importMutation.isPending}
                        indeterminate={someSelected}
                        inputProps={{ 'aria-label': 'Select all importable schedules' }}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Previous Dates</TableCell>
                    <TableCell>Target Dates</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidateGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>No importable schedules derived from Salesforce for this offer</TableCell>
                    </TableRow>
                  ) : (
                    candidateGroups.map((group) => {
                      const first = group.candidates[0];
                      const keys = group.candidates.map((candidate) => candidate.key);
                      const groupChecked = keys.every((key) => selectedKeys.has(key));
                      const groupIndeterminate = !groupChecked && keys.some((key) => selectedKeys.has(key));
                      const result = resultByKey.get(first.key);

                      return (
                        <TableRow key={group.key}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={groupChecked}
                              disabled={first.isMatched || !!first.dependencyError || importMutation.isPending}
                              indeterminate={groupIndeterminate}
                              inputProps={{
                                'aria-label': `Import ${getScheduleTypeLabel(first.type)} for ${first.brandLabel} across ${group.regions.length} region(s)`,
                              }}
                              onChange={() => toggleKeys(keys, !groupChecked)}
                            />
                          </TableCell>
                          <TableCell>{getScheduleTypeLabel(first.type)}</TableCell>
                          <TableCell>{first.brandLabel}</TableCell>
                          <TableCell>
                            <CampaignScheduleValuePillCell label="regions" values={group.regions} />
                          </TableCell>
                          <TableCell>
                            {first.conflictingSchedule
                              ? `${formatDateISO(first.conflictingSchedule.start)} – ${formatDateISO(first.conflictingSchedule.end)}`
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {formatDateISO(first.start)} – {formatDateISO(first.end)}
                          </TableCell>
                          <TableCell>
                            {result ? (
                              <Chip
                                color={result.success ? 'success' : 'error'}
                                label={
                                  result.success ? (first.conflictingSchedule ? 'Updated' : 'Imported') : result.error
                                }
                                size="small"
                                variant="outlined"
                              />
                            ) : first.isMatched ? (
                              <Chip label="Already exists" size="small" variant="outlined" />
                            ) : first.dependencyError ? (
                              <Chip color="warning" label={first.dependencyError} size="small" variant="outlined" />
                            ) : first.conflictingSchedule ? (
                              <Chip
                                color="info"
                                label="Will update existing schedule"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="Will create" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          disabled={selectedKeys.size === 0 || importMutation.isPending || !offerId}
          variant="contained"
          onClick={handleImport}
        >
          {importMutation.isPending ? 'Importing…' : `Import selected (${selectedKeys.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
